import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import { authApi } from '@/api/auth';
import { User } from '@/types';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  loginUser: (email: string, password: string, redirect?: string) => Promise<{ success: boolean; error?: string; redirect_to?: string }>;
  logoutUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        setIsLoading(true);
        const currentUser = await authApi.getCurrentUser(); // Fetch the current user
        setUser(currentUser);
      } catch (error) {
        console.error('Error fetching authenticated user:', error);
        setUser(null); // Clear user state if not authenticated
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const loginUser = async (email: string, password: string, redirect?: string) => {
    try {
      setIsLoading(true);
      // forward redirect param to backend; backend may return canonical redirect_to
      const { user, redirect_to } = await authApi.login(email, password, redirect);
      setUser(user); // Set the authenticated user
      // return server response so caller can navigate to redirect_to if present
      return { success: true, redirect_to } as { success: boolean; error?: string; redirect_to?: string };
    } catch (error: any) {
      console.error('Login error:', error.message);
      return { success: false, error: error.message || 'Login failed' };
    } finally {
      setIsLoading(false);
    }
  };

  const logoutUser = async () => {
    try {
      setIsLoading(true);
      await authApi.logout(); // Use authApi.logout
      setUser(null); // Clear user state
    } catch (error: any) {
      console.error('Logout error:', error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, isLoading, loginUser, logoutUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};